/**
 * Login Page
 *
 * User authentication page with 2FA support
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock } from 'lucide-react'

import { useAuth } from '@/application/auth'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Card, CardHeader, CardContent, CardFooter } from '@/presentation/components/ui/Card'
import { Login2FAModal } from '@/presentation/components/auth/Login2FAModal'
import { LoginLogo } from '@/presentation/components/ui/AppLogo'
import { APP_ROUTES } from '@/config/constants'
import { env } from '@/config/env'
import { PRODUCTO } from '@/config/producto'

// =============================================================================
// Validation Schema
// =============================================================================

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'El email es requerido')
    .email('Email inválido'),
  password: z
    .string()
    .min(1, 'La contraseña es requerida')
    .min(8, 'La contraseña debe tener al menos 8 caracteres'),
})

type LoginFormData = z.infer<typeof loginSchema>

// =============================================================================
// Component
// =============================================================================

export function LoginPage() {
  const {
    login,
    isLoading,
    error,
    clearError,
    pending2FA,
    cancel2FALogin,
  } = useAuth()
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    clearError()
    try {
      await login(data)
    } catch {
      // Error is handled by context
    }
  }

  // Handle 2FA completion
  const handle2FASuccess = () => {
    // Navigation is handled by the context
  }

  const handle2FACancel = () => {
    cancel2FALogin()
  }

  return (
    /* POR QUE NO `items-center`
       =========================
       Con `items-center`, un contenido mas alto que la pantalla se desborda
       por ARRIBA Y POR ABAJO a la vez, y la parte de arriba queda
       inalcanzable: no se puede subir con el dedo. Es exactamente el
       "se corta arriba y abajo" que se reporto desde un iPhone.

       La forma que si funciona es margen automatico en el hijo: centra
       cuando sobra sitio, y cuando no cabe el margen se reduce a cero y la
       pagina se desplaza con normalidad. */
    <div className="min-h-screen alto-de-pantalla flex flex-col overflow-y-auto bg-gradient-to-br from-[var(--color-primary-950)] via-[var(--color-primary-900)] to-[var(--color-primary-800)] p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 neural-bg opacity-50" />

      {/* Main Card */}
      <Card className="w-full max-w-md relative z-10 my-auto mx-auto animate-fade-in">
        <CardHeader className="space-y-4 text-center">
          {/* Logo */}
          <div className="flex justify-center">
            <LoginLogo subtitle={PRODUCTO.descriptor} />
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {/* Error Alert */}
            {error && (
              <div className="p-3 rounded-lg bg-[var(--destructive)]/10 border border-[var(--destructive)]/20 text-[var(--destructive)] text-sm animate-slide-down">
                {error}
              </div>
            )}

            {/* Email Input */}
            <Input
              {...register('email')}
              type="email"
              label="Correo electrónico"
              placeholder="usuario@ejemplo.com"
              error={errors.email?.message}
              leftIcon={<Mail className="h-4 w-4" />}
              autoComplete="email"
              disabled={isLoading}
            />

            {/* Password Input */}
            <Input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              label="Contraseña"
              placeholder="••••••••"
              error={errors.password?.message}
              leftIcon={<Lock className="h-4 w-4" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="hover:text-[var(--foreground)] transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              }
              autoComplete="current-password"
              disabled={isLoading}
            />

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link
                to={APP_ROUTES.FORGOT_PASSWORD}
                className="text-sm text-[var(--primary)] hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </CardContent>

          <CardFooter>
            <Button
              type="submit"
              className="w-full"
              size="lg"
              isLoading={isLoading}
            >
              Iniciar Sesión
            </Button>
          </CardFooter>
        </form>
      </Card>

      {/* Pie en el flujo, no con posicion absoluta.
          Pegado al fondo del contenedor, en una pantalla corta se montaba
          encima de la tarjeta o quedaba fuera de lo visible. */}
      <div className="relative z-10 shrink-0 pt-6 text-center text-[var(--color-primary-300)] text-sm">
        <p>&copy; {new Date().getFullYear()} {env.APP_NAME}. Todos los derechos reservados.</p>
        <p className="text-xs mt-1 opacity-70">v{env.APP_VERSION} · {__MARCA_DE_COMPILACION__}</p>
      </div>

      {/* 2FA Modal */}
      {pending2FA && (
        <Login2FAModal
          open={true}
          userId={pending2FA.userId}
          userEmail={pending2FA.email}
          onSuccess={handle2FASuccess}
          onCancel={handle2FACancel}
        />
      )}
    </div>
  )
}
